import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  UserPlus,
  UserMinus,
  FileStack,
  BarChart3,
  ScrollText,
  Users,
  AlertTriangle,
  TrendingDown,
  UserCog,
} from "lucide-react";
import { getLifecycleDashboard } from "./lifecycleApi";
import LcOnboarding from "./LcOnboarding";
import LcOffboarding from "./LcOffboarding";
import LcTemplates from "./LcTemplates";
import LcExitAnalytics from "./LcExitAnalytics";
import LcAuditLogs from "./LcAuditLogs";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import PeoplePageShell from "../Employees/PeoplePageShell";
import "../../styles/lifecycle-page.css";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "onboarding", label: "Onboarding", icon: UserPlus },
  { id: "offboarding", label: "Offboarding", icon: UserMinus },
  { id: "templates", label: "Templates", icon: FileStack },
  { id: "analytics", label: "Exit Analytics", icon: BarChart3 },
  { id: "audit", label: "Audit Logs", icon: ScrollText },
];

function MetricCard({ title, value, icon: Icon, accent, onClick }) {
  return (
    <button
      type="button"
      className={`lc-metric-card lc-metric-card--${accent}`}
      onClick={onClick}
      style={{ textAlign: "left", width: "100%" }}
    >
      <p className="lc-metric-label">{title}</p>
      <p className="lc-metric-value">{value ?? "—"}</p>
      <div className={`lc-metric-icon lc-metric-icon--${accent}`}>
        <Icon size={16} />
      </div>
    </button>
  );
}

function DashboardMetrics({ data, onNavigate }) {
  const ob = data?.onboarding || {};
  const off = data?.offboarding || {};
  const wf = data?.workforce || {};

  return (
    <div>
      <p className="lc-section-title">Onboarding</p>
      <div className="lc-metrics-grid">
        <MetricCard title="New Joiners" value={ob.new_joiners ?? ob.new_joiners_this_month} icon={UserPlus} accent="blue" onClick={() => onNavigate("onboarding")} />
        <MetricCard title="Pending Onboardings" value={ob.pending_onboardings} icon={Users} accent="blue" onClick={() => onNavigate("onboarding")} />
        <MetricCard title="Delayed Tasks" value={ob.delayed_tasks} icon={AlertTriangle} accent="amber" onClick={() => onNavigate("onboarding")} />
        <MetricCard title="Upcoming Joinings" value={ob.upcoming_joinings} icon={UserPlus} accent="violet" onClick={() => onNavigate("onboarding")} />
      </div>

      <p className="lc-section-title">Offboarding</p>
      <div className="lc-metrics-grid">
        <MetricCard title="Notice Period" value={off.notice_period_employees} icon={Users} accent="sky" onClick={() => onNavigate("offboarding")} />
        <MetricCard title="Pending Clearances" value={off.pending_clearances} icon={AlertTriangle} accent="red" onClick={() => onNavigate("offboarding")} />
        <MetricCard title="Pending F&F" value={off.fnf_pending} icon={TrendingDown} accent="purple" onClick={() => onNavigate("offboarding")} />
        <MetricCard title="Exits This Month" value={off.exits_this_month} icon={UserMinus} accent="orange" onClick={() => onNavigate("offboarding")} />
      </div>

      <p className="lc-section-title">Workforce</p>
      <div className="lc-metrics-grid">
        <MetricCard title="Active Employees" value={wf.active_employees} icon={Users} accent="blue" onClick={() => onNavigate("onboarding")} />
        <MetricCard title="Joined This Year" value={wf.joined_this_year} icon={UserPlus} accent="indigo" onClick={() => onNavigate("onboarding")} />
        <MetricCard title="Exited This Year" value={wf.exited_this_year} icon={UserMinus} accent="rose" />
        <MetricCard title="Attrition Rate %" value={wf.attrition_rate} icon={TrendingDown} accent="indigo" onClick={() => onNavigate("analytics")} />
      </div>
    </div>
  );
}

export default function LifecycleDashboard({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getLifecycleDashboard()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, []);

  const setTab = (tab) => {
    const params = new URLSearchParams(searchParams);
    if (tab === "dashboard") {
      params.delete("tab");
    } else {
      params.set("tab", tab);
    }
    params.delete("module");
    setSearchParams(params);
  };

  const renderContent = () => {
    if (activeTab === "dashboard") {
      if (loading) {
        return <BrixigoPageLoader compact message="Loading dashboard…" />;
      }
      if (error) return <p className="text-red-600">{error}</p>;
      return <DashboardMetrics data={data} onNavigate={setTab} />;
    }

    const panels = {
      onboarding: <LcOnboarding />,
      offboarding: <LcOffboarding />,
      templates: <LcTemplates />,
      analytics: <LcExitAnalytics />,
      audit: <LcAuditLogs />,
    };

    return <div className="lc-panel">{panels[activeTab]}</div>;
  };

  const tabsNav = (
    <nav className="lc-tabs" aria-label="Lifecycle sections">
      {SECTIONS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          className={`lc-tab lc-tab--${id} ${activeTab === id ? "lc-tab--active" : ""}`}
          onClick={() => setTab(id)}
        >
          <Icon className="lc-tab-icon" />
          {label}
        </button>
      ))}
    </nav>
  );

  const ob = data?.onboarding || {};
  const off = data?.offboarding || {};
  const wf = data?.workforce || {};
  const heroStats = !loading && !error ? (
    <>
      <span className="people-page-chip">
        <Users size={14} />
        <strong>{wf.active_employees ?? "—"}</strong> active
      </span>
      <span className="people-page-chip">
        <UserPlus size={14} />
        <strong>{ob.pending_onboardings ?? "—"}</strong> onboarding
      </span>
      <span className="people-page-chip">
        <UserMinus size={14} />
        <strong>{off.notice_period_employees ?? "—"}</strong> notice period
      </span>
    </>
  ) : null;

  const body = (
    <>
      {tabsNav}
      {renderContent()}
    </>
  );

  if (embedded) {
    return <div className="lc-embedded">{body}</div>;
  }

  return (
    <PeoplePageShell
      eyebrow="People operations"
      title="Employee Lifecycle"
      subtitle="Onboarding, offboarding, clearance and settlement — in one dedicated workspace."
      icon={UserCog}
      stats={heroStats}
    >
      {body}
    </PeoplePageShell>
  );
}
